var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, Grid, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, } from '@mui/material';
import { Info as DetailsIcon, Build as ServiceIcon, Assignment as JobsIcon, ArrowBack as BackIcon, GetApp as ExportIcon, Delete as DeleteIcon, } from '@mui/icons-material';
import { machineService } from '@services/machineService';
import { formatDateForFilename } from '@utils/dateUtils';
const MachineDetails = () => {
    const { machineId } = useParams();
    const navigate = useNavigate();
    const [machine, setMachine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    useEffect(() => {
        loadMachine();
    }, [machineId]);
    const loadMachine = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            const data = yield machineService.getById(Number(machineId));
            setMachine(data);
        }
        catch (error) {
            console.error('Error loading machine:', error);
        }
        finally {
            setLoading(false);
        }
    });
    const handleNavigation = (path) => {
        navigate(path);
    };
    const handleExportMachine = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const blob = yield machineService.exportMachineDetails(Number(machineId));
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Machine_${(machine === null || machine === void 0 ? void 0 : machine.name) || 'Export'}_${formatDateForFilename()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setSnackbar({ open: true, message: 'Machine data exported successfully', severity: 'success' });
        }
        catch (error) {
            console.error('Error exporting machine:', error);
            setSnackbar({ open: true, message: 'Error exporting machine data', severity: 'error' });
        }
    });
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield machineService.delete(Number(machineId));
            setSnackbar({ open: true, message: 'Machine deleted successfully', severity: 'success' });
            setDeleteDialogOpen(false);
            // Navigate back to machines list after successful deletion
            setTimeout(() => navigate('/machines'), 1000);
        }
        catch (error) {
            console.error('Error deleting machine:', error);
            setSnackbar({ open: true, message: 'Error deleting machine', severity: 'error' });
            setDeleteDialogOpen(false);
        }
    });
    if (loading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }, children: _jsx(CircularProgress, {}) }));
    }
    return (_jsxs(Box, { sx: { px: { xs: 1, sm: 2 } }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 3,
                    gap: 2
                }, children: [_jsx(Button, { startIcon: _jsx(BackIcon, {}), onClick: () => navigate('/machines'), sx: { width: { xs: '100%', sm: 'auto' } }, children: "Back to Machines" }), _jsxs(Box, { sx: {
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2
                        }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(ExportIcon, {}), onClick: handleExportMachine, sx: { width: { xs: '100%', sm: 'auto' } }, children: "Export Machine" }), _jsx(Button, { variant: "outlined", color: "error", startIcon: _jsx(DeleteIcon, {}), onClick: handleDeleteClick, sx: { width: { xs: '100%', sm: 'auto' } }, children: "Delete Machine" })] })] }), _jsxs(Paper, { sx: { p: { xs: 2, sm: 3 } }, children: [_jsx(Typography, { variant: "h5", sx: { mb: 4, textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }, children: (machine === null || machine === void 0 ? void 0 : machine.name) || 'Machine Options' }), _jsxs(Grid, { container: true, spacing: 3, justifyContent: "center", children: [_jsx(Grid, { item: true, xs: 12, sm: 12, md: 12, children: _jsx(Button, { variant: "contained", fullWidth: true, onClick: () => handleNavigation(`/machines/${machineId}/details`), startIcon: _jsx(DetailsIcon, {}), sx: {
                                        py: 4,
                                        fontSize: '1.2rem',
                                        flexDirection: 'column',
                                        gap: 1,
                                        '& .MuiButton-startIcon': {
                                            margin: 0,
                                            '& > svg': {
                                                fontSize: '3rem',
                                            },
                                        },
                                    }, children: "Details" }) }), _jsx(Grid, { item: true, xs: 12, sm: 12, md: 12, children: _jsx(Button, { variant: "contained", fullWidth: true, onClick: () => handleNavigation(`/machines/${machineId}/service-history`), startIcon: _jsx(ServiceIcon, {}), sx: {
                                        py: 4,
                                        fontSize: '1.2rem',
                                        flexDirection: 'column',
                                        gap: 1,
                                        '& .MuiButton-startIcon': {
                                            margin: 0,
                                            '& > svg': {
                                                fontSize: '3rem',
                                            },
                                        },
                                    }, children: "Service History" }) }), _jsx(Grid, { item: true, xs: 12, sm: 12, md: 12, children: _jsx(Button, { variant: "contained", fullWidth: true, onClick: () => handleNavigation(`/machines/${machineId}/jobs`), startIcon: _jsx(JobsIcon, {}), sx: {
                                        py: 4,
                                        fontSize: '1.2rem',
                                        flexDirection: 'column',
                                        gap: 1,
                                        '& .MuiButton-startIcon': {
                                            margin: 0,
                                            '& > svg': {
                                                fontSize: '3rem',
                                            },
                                        },
                                    }, children: "Jobs" }) })] })] }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: handleDeleteCancel, "aria-labelledby": "delete-dialog-title", "aria-describedby": "delete-dialog-description", children: [_jsx(DialogTitle, { id: "delete-dialog-title", children: "Delete Machine" }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { id: "delete-dialog-description", children: ["Are you sure you want to delete ", _jsx("strong", { children: machine === null || machine === void 0 ? void 0 : machine.name }), "?", _jsx("br", {}), _jsx("br", {}), "This action will permanently delete:", _jsx("br", {}), "\u2022 The machine", _jsx("br", {}), "\u2022 All service history records", _jsx("br", {}), "\u2022 All associated jobs", _jsx("br", {}), _jsx("br", {}), "This action cannot be undone."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleDeleteCancel, color: "primary", children: "Cancel" }), _jsx(Button, { onClick: handleDeleteConfirm, color: "error", variant: "contained", children: "Delete" })] })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default MachineDetails;
